import type { Card, GameState } from '../game/types';
import type { EnemyAiDecision, EnemyAiDecisionContext } from './types';

type EnemyBehaviorConfig = {
  chargeSetupTitle?: string;
  chargePayoffTitle?: string;
  defenseTitles?: string[];
  cooldowns?: Record<string, number>;
};

const CONFIGS: Record<string, EnemyBehaviorConfig> = {
  cultist: {
    chargeSetupTitle: 'sprint',
    chargePayoffTitle: 'power strike',
    defenseTitles: ['defensive stance'],
  },
  corrupted_cultist: {
    chargeSetupTitle: 'sprint',
    chargePayoffTitle: 'power strike',
    defenseTitles: ['defensive stance'],
  },
  hexbinder: {
    chargeSetupTitle: 'meditation',
    chargePayoffTitle: 'ritual',
    defenseTitles: ['ward'],
  },
  chymist: {},
  'chained-penitent': {
    defenseTitles: ['chain armor'],
  },
  mercenary: {
    defenseTitles: ['smoke bomb'],
  },
  dreadbrand: {
    chargeSetupTitle: 'sprint',
    chargePayoffTitle: 'iron rend',
    defenseTitles: ['defensive stance'],
    cooldowns: {
      'dark edict': 3,
    },
  },
  'dark-apostle': {},
  yahaka_voidfeeder_1: {
    chargeSetupTitle: 'crouching stance',
    chargePayoffTitle: 'call of the void',
    defenseTitles: ['thick fur'],
  },
  yahaka_voidfeeder_2: {
    chargeSetupTitle: 'crouching stance',
    chargePayoffTitle: 'call of the void',
    defenseTitles: ['thick fur'],
  },
  yahaka_voidfeeder_3: {
    chargeSetupTitle: 'crouching stance',
    chargePayoffTitle: 'call of the void',
    defenseTitles: ['thick fur'],
  },
  yahaka_voidfeeder_4: {
    chargeSetupTitle: 'crouching stance',
    chargePayoffTitle: 'call of the void',
    defenseTitles: ['thick fur'],
  },
  yahaka_voidfeeder_5: {
    chargeSetupTitle: 'crouching stance',
    chargePayoffTitle: 'call of the void',
    defenseTitles: ['thick fur'],
  },
  yahaka_voidfeeder_6: {
    chargeSetupTitle: 'crouching stance',
    chargePayoffTitle: 'call of the void',
    defenseTitles: ['thick fur'],
  },
  yahaka_voidfeeder_7: {
    chargeSetupTitle: 'crouching stance',
    chargePayoffTitle: 'call of the void',
    defenseTitles: ['thick fur'],
  },
  nested_horror: {
    defenseTitles: ['web cocoon'],
  },
};

function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

export function normEnemyAiValue(value: unknown): string {
  return norm(value);
}

export function titleOfEnemyAiCard(card: Card | null | undefined): string {
  return norm(card?.title ?? (card as any)?.name ?? card?.id);
}

function getConfig(enemyId: string | undefined | null): EnemyBehaviorConfig | null {
  const key = norm(enemyId);
  return CONFIGS[key] ?? null;
}

function sanitizeHistory(history: unknown): string[] {
  if (!Array.isArray(history)) return [];
  return history.map((entry) => norm(entry)).filter(Boolean);
}

export function getEnemyAiCombatHistory(state: GameState): string[] {
  return sanitizeHistory((state as any).enemyAiCombatHistory);
}

export function getEnemyAiCurrentTurnActions(state: GameState): string[] {
  return sanitizeHistory((state as any).enemyAiCurrentTurnActions);
}

export function getEnemyAiLastTurnActions(state: GameState): string[] {
  return sanitizeHistory((state as any).enemyAiLastTurnActions);
}

export function trailingSameCount(history: string[], title: string): number {
  const cleanTitle = norm(title);
  let count = 0;
  for (let i = history.length - 1; i >= 0; i -= 1) {
    if (norm(history[i]) !== cleanTitle) break;
    count += 1;
  }
  return count;
}

function seedFromState(state: GameState, salt: string): number {
  let seed = Math.imul((Number((state as any).turn ?? 0) || 0) + 1, 1103515245) >>> 0;
  seed = (seed ^ Math.imul((Number(state.player?.hp ?? 0) || 0) + 3, 2654435761)) >>> 0;
  seed = (seed ^ Math.imul((Number(state.player?.armor ?? 0) || 0) + 5, 2246822519)) >>> 0;
  seed = (seed ^ Math.imul((Number(state.enemy?.hp ?? 0) || 0) + 7, 3266489917)) >>> 0;
  seed = (seed ^ Math.imul((Number(state.enemy?.armor ?? 0) || 0) + 11, 668265263)) >>> 0;
  const history = getEnemyAiCombatHistory(state).slice(-8).join('|');
  const text = `${salt}|${String(state.enemyId ?? '')}|${history}`;
  for (let i = 0; i < text.length; i += 1) {
    seed = (seed ^ text.charCodeAt(i)) >>> 0;
    seed = Math.imul(seed, 16777619) >>> 0;
  }
  return seed >>> 0;
}

function chooseDeterministicIndex(length: number, state: GameState, salt: string): number {
  if (length <= 1) return 0;
  return seedFromState(state, salt) % length;
}

function chance(state: GameState, salt: string, threshold01: number): boolean {
  const value = (seedFromState(state, salt) % 10000) / 10000;
  return value < threshold01;
}

function chooseOne(cards: Card[], state: GameState, salt: string): Card | null {
  if (!cards.length) return null;
  return cards[chooseDeterministicIndex(cards.length, state, salt)] ?? null;
}

function sumHealAmount(card: Card | null | undefined): number {
  if (!card || !Array.isArray(card.effects)) return 0;
  let total = 0;
  for (const effect of card.effects as any[]) {
    const type = String(effect?.type ?? '').trim();
    if (type === 'healHP') total += Math.max(0, Math.floor(Number(effect?.amount) || 0));
  }
  return total;
}

function hasDamageEffect(card: Card | null | undefined): boolean {
  return getDamageProfile(card, null).damage > 0;
}

function hasStunEffect(card: Card | null | undefined): boolean {
  if (!card || !Array.isArray(card.effects)) return false;
  return (card.effects as any[]).some((effect) => String(effect?.type ?? '').trim() === 'stun');
}

function isStunAttackCard(card: Card | null | undefined): boolean {
  return hasDamageEffect(card) && hasStunEffect(card);
}

function isDefenseCard(card: Card | null | undefined, config: EnemyBehaviorConfig): boolean {
  const title = titleOfEnemyAiCard(card);
  if (config.defenseTitles?.includes(title)) return true;
  if (!card || !Array.isArray(card.effects)) return false;
  return (card.effects as any[]).some((effect) => {
    const type = String(effect?.type ?? '').trim();
    return type === 'block' || type === 'setArmor' || type === 'doubleArmor' || type === 'blockPerPlaysThisTurn';
  });
}

function getDamageProfile(card: Card | null | undefined, state: GameState | null): { damage: number; ignoresArmor: boolean } {
  const title = titleOfEnemyAiCard(card);

  if (title === 'deadeye shot') {
    const empowered = !!((state as any)?.mercenaryPrecision);
    return { damage: empowered ? 36 : 18, ignoresArmor: false };
  }

  if (title === 'gutting strike') {
    const empowered = !!((state as any)?.mercenaryAmbush);
    return { damage: empowered ? 34 : 16, ignoresArmor: true };
  }

  let damage = 0;
  let ignoresArmor = false;

  if (card && Array.isArray(card.effects)) {
    for (const effect of card.effects as any[]) {
      const type = String(effect?.type ?? '').trim();
      if (type === 'damage') {
        damage += Math.max(0, Math.floor(Number(effect?.amount) || 0));
      } else if (type === 'damageIgnoreArmor') {
        damage += Math.max(0, Math.floor(Number(effect?.amount) || 0));
        ignoresArmor = true;
      } else if (type === 'damageEqualToArmor') {
        damage += Math.max(0, Math.floor(Number((state?.enemy as any)?.armor ?? 0) || 0));
      } else if (type === 'damageEqualToReflect') {
        damage += Math.max(0, Math.floor(Number((state?.enemy as any)?.reflectPct ?? 0) || 0));
      }
    }
  }

  return { damage, ignoresArmor };
}

function isAttackCard(card: Card | null | undefined, state: GameState): boolean {
  return getDamageProfile(card, state).damage > 0;
}

function isPureHealCard(card: Card | null | undefined, state: GameState, config: EnemyBehaviorConfig): boolean {
  return sumHealAmount(card) > 0 && !isAttackCard(card, state) && !isDefenseCard(card, config);
}

function enemyMaxHpBase(state: GameState): number {
  return Math.max(
    1,
    Math.floor(
      Number((state as any).enemyMaxHpBase ?? (state.enemy as any)?.maxHp ?? state.enemy?.hp ?? 1) || 1,
    ),
  );
}

function enemyMissingHp(state: GameState): number {
  return Math.max(0, enemyMaxHpBase(state) - Math.max(0, Number(state.enemy?.hp ?? 0) || 0));
}

function hasUsableHeal(card: Card | null | undefined, state: GameState): boolean {
  const heal = sumHealAmount(card);
  if (heal <= 0) return false;
  const missing = enemyMissingHp(state);
  if (missing <= 0) return false;
  return missing >= Math.ceil(heal * 0.5);
}

function getCardCooldownTurns(card: Card | null | undefined, config: EnemyBehaviorConfig): number {
  const title = titleOfEnemyAiCard(card);
  const explicit = Math.max(0, Math.floor(Number(config.cooldowns?.[title] ?? 0) || 0));
  if (explicit > 0) return explicit;
  if (isStunAttackCard(card)) return 3;
  return 0;
}

function cardIsOnCooldown(card: Card | null | undefined, state: GameState, config: EnemyBehaviorConfig): boolean {
  const title = titleOfEnemyAiCard(card);
  const turns = getCardCooldownTurns(card, config);
  if (turns <= 0) return false;
  const currentTurn = getEnemyAiCurrentTurnActions(state);
  if (currentTurn.includes(title)) return true;
  const history = getEnemyAiCombatHistory(state);
  return history.slice(-turns).includes(title);
}

function canLethal(card: Card | null | undefined, state: GameState): boolean {
  const profile = getDamageProfile(card, state);
  if (profile.damage <= 0) return false;
  const playerHp = Math.max(0, Math.floor(Number(state.player?.hp ?? 0) || 0));
  const playerArmor = Math.max(0, Math.floor(Number(state.player?.armor ?? 0) || 0));
  if (profile.ignoresArmor) return profile.damage >= playerHp;
  return profile.damage >= playerHp + playerArmor;
}

function chooseForcedChargeCard(cards: Card[], state: GameState, config: EnemyBehaviorConfig): Card | null {
  const setupTitle = norm(config.chargeSetupTitle);
  const payoffTitle = norm(config.chargePayoffTitle);
  if (!setupTitle || !payoffTitle) return null;

  const history = getEnemyAiCombatHistory(state);
  const last = history[history.length - 1] ?? '';
  const prev = history[history.length - 2] ?? '';

  const setupCards = cards.filter((card) => titleOfEnemyAiCard(card) === setupTitle);
  const payoffCards = cards.filter((card) => titleOfEnemyAiCard(card) === payoffTitle);

  if (last === setupTitle && prev === setupTitle && payoffCards.length > 0) {
    return chooseOne(payoffCards, state, `forced-payoff:${payoffTitle}`);
  }

  if (last === setupTitle && setupCards.length > 0) {
    return chooseOne(setupCards, state, `forced-second-setup:${setupTitle}`);
  }

  return null;
}


function chooseForcedMercenaryPayoff(cards: Card[], state: GameState): Card | null {
  if (norm(state.enemyId) !== 'mercenary') return null;

  const precision = !!((state as any).mercenaryPrecision);
  const ambush = !!((state as any).mercenaryAmbush);

  const deadeyeCards = cards.filter((card) => titleOfEnemyAiCard(card) === 'deadeye shot');
  const guttingCards = cards.filter((card) => titleOfEnemyAiCard(card) === 'gutting strike');

  if (precision && deadeyeCards.length > 0) {
    return chooseOne(deadeyeCards, state, 'mercenary-precision-payoff');
  }

  if (ambush && guttingCards.length > 0) {
    return chooseOne(guttingCards, state, 'mercenary-ambush-payoff');
  }

  return null;
}

function chooseSmallReflectAttack(cards: Card[], state: GameState): Card | null {
  const attacks = cards
    .map((card) => ({ card, profile: getDamageProfile(card, state) }))
    .filter((entry) => entry.profile.damage > 0);

  if (!attacks.length) return null;

  attacks.sort((a, b) => {
    if (a.profile.damage !== b.profile.damage) return a.profile.damage - b.profile.damage;
    return titleOfEnemyAiCard(a.card).localeCompare(titleOfEnemyAiCard(b.card));
  });

  const minDamage = attacks[0]?.profile.damage ?? 0;
  const smallest = attacks.filter((entry) => entry.profile.damage === minDamage).map((entry) => entry.card);
  return chooseOne(smallest, state, 'reflect-smallest-attack');
}

function hasDoubleChargePrimed(state: GameState, config: EnemyBehaviorConfig): boolean {
  const setupTitle = norm(config.chargeSetupTitle);
  const payoffTitle = norm(config.chargePayoffTitle);
  if (!setupTitle || !payoffTitle) return false;
  const history = getEnemyAiCombatHistory(state);
  const last = history[history.length - 1] ?? '';
  const prev = history[history.length - 2] ?? '';
  return last === setupTitle && prev === setupTitle;
}

function isLockedChargePayoff(card: Card | null | undefined, state: GameState, config: EnemyBehaviorConfig): boolean {
  const payoffTitle = norm(config.chargePayoffTitle);
  if (!payoffTitle) return false;
  if (titleOfEnemyAiCard(card) !== payoffTitle) return false;
  return !hasDoubleChargePrimed(state, config);
}

function isLockedMercenaryPayoff(card: Card | null | undefined, state: GameState): boolean {
  if (norm(state.enemyId) !== 'mercenary') return false;
  const title = titleOfEnemyAiCard(card);
  if (title === 'deadeye shot') return !((state as any).mercenaryPrecision);
  if (title === 'gutting strike') return !((state as any).mercenaryAmbush);
  return false;
}

function wouldBeThirdConsecutive(card: Card | null | undefined, state: GameState): boolean {
  const title = titleOfEnemyAiCard(card);
  if (!title) return false;
  return trailingSameCount(getEnemyAiCombatHistory(state), title) >= 2;
}

function countCardUsesByTitle(cards: Card[], state: GameState): Map<string, number> {
  const counts = new Map<string, number>();
  const history = getEnemyAiCombatHistory(state);
  for (const card of cards) {
    const title = titleOfEnemyAiCard(card);
    if (!title) continue;
    if (!counts.has(title)) counts.set(title, 0);
  }
  for (const entry of history) {
    if (!counts.has(entry)) continue;
    counts.set(entry, (counts.get(entry) ?? 0) + 1);
  }
  return counts;
}

function preferDiverseCards(cards: Card[], state: GameState, salt: string): Card | null {
  if (!cards.length) return null;
  const counts = countCardUsesByTitle(cards, state);
  let bestScore = Number.POSITIVE_INFINITY;
  const scored = cards.map((card) => {
    const title = titleOfEnemyAiCard(card);
    const uses = counts.get(title) ?? 0;
    const recentPenalty = trailingSameCount(getEnemyAiCombatHistory(state), title) * 100;
    const score = uses * 1000 + recentPenalty;
    if (score < bestScore) bestScore = score;
    return { card, score };
  });
  const best = scored.filter((entry) => entry.score === bestScore).map((entry) => entry.card);
  return chooseOne(best, state, salt);
}

function filterEligibleCards(cards: Card[], state: GameState, config: EnemyBehaviorConfig): Card[] {
  const noThirdConsecutive = cards.filter((card) => !wouldBeThirdConsecutive(card, state));
  const repeatBase = noThirdConsecutive.length > 0 ? noThirdConsecutive : cards;

  const noLockedPayoffs = repeatBase.filter((card) => !isLockedChargePayoff(card, state, config) && !isLockedMercenaryPayoff(card, state));
  const payoffBase = noLockedPayoffs.length > 0 ? noLockedPayoffs : repeatBase;

  const withoutCooldown = payoffBase.filter((card) => !cardIsOnCooldown(card, state, config));
  const cooldownBase = withoutCooldown.length > 0 ? withoutCooldown : payoffBase;

  const withoutBadPureHeals = cooldownBase.filter((card) => !isPureHealCard(card, state, config) || hasUsableHeal(card, state));
  return withoutBadPureHeals.length > 0 ? withoutBadPureHeals : cooldownBase;
}

export function hasGenericEnemyAi(enemyId: string | undefined | null): boolean {
  return !!getConfig(enemyId);
}

export function pickGenericEnemyCard(context: EnemyAiDecisionContext): EnemyAiDecision {
  const { state, availableCards } = context;
  const config = getConfig(state.enemyId);
  if (!config || !Array.isArray(availableCards) || availableCards.length === 0) {
    return { card: null };
  }

  const eligible = filterEligibleCards(availableCards, state, config);
  if (!eligible.length) return { card: availableCards[0] ?? null, reason: 'fallback-no-eligible-cards' };

  const lethalCards = eligible.filter((card) => canLethal(card, state));
  if (lethalCards.length > 0 && chance(state, 'lethal-70', 0.7)) {
    return {
      card: preferDiverseCards(lethalCards, state, 'lethal'),
      reason: 'lethal-70-percent',
    };
  }

  const forcedMercenaryPayoff = chooseForcedMercenaryPayoff(eligible, state);
  if (forcedMercenaryPayoff) {
    return {
      card: forcedMercenaryPayoff,
      reason: 'mercenary-forced-setup-payoff',
    };
  }

  const forcedCharge = chooseForcedChargeCard(eligible, state, config);
  if (forcedCharge) {
    return {
      card: forcedCharge,
      reason: 'forced-charge-sequence',
    };
  }

  const lastPlayerTurnDamageToEnemy = Math.max(0, Math.floor(Number((state as any).lastPlayerTurnDamageToEnemy ?? 0) || 0));
  const tookBigDamageLastTurn = lastPlayerTurnDamageToEnemy > enemyMaxHpBase(state) * 0.4;

  if (tookBigDamageLastTurn) {
    const survivalPool = eligible.filter((card) => isDefenseCard(card, config) || hasUsableHeal(card, state));
    if (survivalPool.length > 0) {
      return {
        card: preferDiverseCards(survivalPool, state, 'survival'),
        reason: 'big-damage-last-turn',
      };
    }
  }

  const playerReflectPct = Math.max(0, Math.floor(Number(state.player?.reflectPct ?? 0) || 0));
  if (playerReflectPct > 0 && chance(state, 'reflect-trigger', 0.75)) {
    const smallAttack = chooseSmallReflectAttack(eligible, state);
    if (smallAttack) {
      return {
        card: smallAttack,
        reason: 'trigger-reflect-with-small-attack',
      };
    }
  }

  return {
    card: preferDiverseCards(eligible, state, 'random-diverse'),
    reason: 'random',
  };
}
